/**
 * Pure label helpers for the dashboard time-range controls, extracted so they
 * can be unit-tested without rendering. `t` is the i18next translate function;
 * typed minimally here so the helpers stay decoupled from the i18n setup.
 */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Label for a single option row in the From/To columns.
 *
 * - `0h` -> "now"
 * - whole days (multiples of 24h) -> "{{days}} day(s)" (singular/plural)
 * - anything else -> "{{hours}} hours"
 */
export function formatTimeOptionLabel(hours: number, t: TranslateFn): string {
  if (hours === 0) return t("common.now");

  if (hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? t("common.days_singular", { days }) : t("common.days", { days });
  }

  return t("common.hours", { hours });
}

/**
 * Compact bound label used in the toolbar trigger: `now` for 0h, otherwise a
 * signed hour offset (`+8h`, `-4h`).
 */
export function formatTriggerHours(hours: number, t: TranslateFn): string {
  if (hours === 0) return t("common.now");
  const sign = hours > 0 ? "+" : "";
  return `${sign}${hours}h`;
}

/**
 * Combined "from to" label for the toolbar trigger, or `undefined` when either
 * bound is missing (so the caller can hide the range entirely).
 */
export function formatTimeRange(
  hoursStart: number | undefined,
  hoursEnd: number | undefined,
  t: TranslateFn,
): string | undefined {
  if (hoursStart === undefined || hoursEnd === undefined) return undefined;

  return `${formatTriggerHours(hoursStart, t)} ${t("common.to")} ${formatTriggerHours(
    hoursEnd,
    t,
  )}`;
}
